import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';

import { startLinkSession, type LinkSessionError } from '../flow/client';
import { browserId } from '../flow/telemetry';
import { EmptySession } from '../ui/EmptySession';

/**
 * A workflow's reusable link, `/l/<linkId>` — the one an operator copies from
 * the workflow's settings and shares openly. Opening it starts a session (or
 * resumes this browser's unfinished one) and continues at `/u/<code>`, the same
 * entry a partner-minted link uses.
 */
export function LinkScreen() {
  const { linkId } = useParams<{ linkId: string }>();
  const { i18n, t } = useTranslation();
  const [target, setTarget] = useState<string | null>(null);
  const [error, setError] = useState<LinkSessionError | null>(null);

  useEffect(() => {
    if (!linkId) return;
    let active = true;
    void startLinkSession(linkId, browserId(), i18n.language).then((result) => {
      if (!active) return;
      if ('url' in result) {
        // The server builds the link on the project's verify host; continue on
        // THIS origin, which already serves the flow.
        setTarget(new URL(result.url).pathname);
      } else {
        setError(result.error);
      }
    });
    return () => {
      active = false;
    };
  }, [linkId, i18n.language]);

  if (!linkId || error === 'not_found') return <EmptySession kind="missing" />;
  if (target) return <Navigate replace to={target} />;
  if (error) {
    const limit = error === 'retry_limit';
    return (
      <main className="screen screen--empty">
        <div className="card card--empty enter" data-testid="link-error" role="alert">
          <h1 className="empty-title">
            {t(limit ? 'linkEntry.limitTitle' : 'linkEntry.unavailableTitle')}
          </h1>
          <p className="empty-body">
            {t(limit ? 'linkEntry.limitBody' : 'linkEntry.unavailableBody')}
          </p>
        </div>
      </main>
    );
  }
  return (
    <main aria-busy="true" className="screen screen--empty">
      <p className="empty-body">{t('linkEntry.starting')}</p>
    </main>
  );
}
